import { memo } from "react"
import Markdown from "react-markdown"
import remarkGfm from "remark-gfm"
import remarkImages from "remark-images"

function cleanMarkdownUrls(content: string): string {
  if (!content || typeof content !== 'string') return content

  // Fix URLs with escaped backslashes and trailing spaces
  return content.replace(
    /!\[([^\]]*)\]\(([^)]*%5C[^)]*)\s*\)/g,
    (match, altText, url) => {
      // Remove %5C (escaped backslash) and trim spaces
      const cleanUrl = url.replace(/%5C/g, '/').trim()
      return `![${altText}](${cleanUrl})`
    }
  )
}

function cleanMarkdownLists(content: string): string {
  if (!content || typeof content !== 'string') return content

  // Convert "1. **Title**  \nDescription" (two-space soft break) into
  // "1. **Title**\n\nDescription" so react-markdown keeps them as separate paragraphs
  // but the title stays on the same line as the number.
  // Also handle single trailing space before newline.
  let cleaned = content.replace(/(^\d+\. \*\*[^\n]+\*\*)\s*\n(?!\n)/gm, '$1\n\n')


  // Fix bullet points that got separated: "- \n\n**text**" -> "- **text**"
  cleaned = cleaned.replace(/^(-|\*)\s*\n+\s*(\*\*)/gm, '$1 $2')

  return cleaned
}


// Wraps every case-insensitive match of term in rendered text nodes with <mark>
function rehypeHighlight(term: string) {
  const re = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi")
  const walk = (node: any) => {
    if (!node.children) return
    node.children = node.children.flatMap((child: any) => {
      if (child.type !== "text") {
        walk(child)
        return [child]
      }
      const parts: string[] = child.value.split(re)
      if (parts.length === 1) return [child]
      return parts.map((value, i) => i % 2
        ? { type: "element", tagName: "mark", properties: { className: ["bg-yellow-200", "rounded-sm"] }, children: [{ type: "text", value }] }
        : { type: "text", value }
      ).filter((n) => n.type === "element" || n.value)
    })
  }
  return () => (tree: any) => walk(tree)
}


export const ToMarkdown = memo(function ToMarkdown(props: { children: string, variant?: "chat" | "document", highlight?: string }) {

  const doc = props.variant === "document"
  const cleanedValue = cleanMarkdownLists(cleanMarkdownUrls(props.children || ""))


  return <div dir="auto"><Markdown
    components={{
      h1: ({ node, ...rest }) => <h1 className={`font-semibold text-2xl ${doc ? "mb-3" : ""}`} {...rest} />,
      h2: ({ node, ...rest }) => <h2 className={`font-semibold text-xl ${doc ? "mt-6 mb-2" : ""}`} {...rest} />,
      h3: ({ node, ...rest }) => <h3 className={`font-semibold text-lg ${doc ? "mt-4 mb-1" : ""}`} {...rest} />,
      a: ({ node, ...rest }) => <a className="text-blue-600 hover:underline" {...rest} />,
      hr: ({ node, ...rest }) => <hr className="my-2 border-t border-gray-300" {...rest} />,
      img: ({ node, ...props }) => (
        <img
          className="max-w-full h-auto my-2 rounded"
          {...props}
          alt={props.alt || ''}
          loading="lazy"
          onError={(e) => {
            console.error('Image failed to load:', props.src)
            e.currentTarget.style.display = 'none'
          }}
        />
      ),
      p: ({ node, ...rest }) => <span className={`block ${doc ? "mb-1.5 leading-normal" : "leading-tight"}`} {...rest} />,
      ul: ({ node, ...rest }) => <ul className={`list-disc pl-5 ${doc ? "mb-2" : ""}`} {...rest} />,
      ol: ({ node, ...rest }) => <ol className={`list-decimal pl-5 ${doc ? "mb-2" : ""}`} {...rest} />,
      li: ({ node, children, ...rest }) => <li className={`pl-1 ${doc ? "" : "mt-0! mb-0!"}`} {...rest}>{children}</li>,
      blockquote: ({ node, ...rest }) => (
        <blockquote className="border-l-4 border-gray-300 pl-4 italic" {...rest} />
      ),
      code: ({ node, className, children, ...props }: any) => {
        const inline = !node?.position
        if (inline) return <code className="bg-gray-100 px-1 py-0.5 rounded text-sm" {...props}>{children}</code>

        const lang = /language-(\w+)/.exec(className || "")?.[1]
        if (lang === "diff") {
          const lines = String(children).replace(/\n$/, "").split("\n")
          return <code className="block text-sm font-mono">
            {lines.map((line, i) => {
              const cls = line.startsWith("+") ? "bg-green-100 text-green-800" : line.startsWith("-") ? "bg-red-100 text-red-800" : "text-gray-600"
              return <span key={i} className={`block whitespace-pre-wrap wrap-break-word ${cls}`}>{line || " "}</span>
            })}
          </code>
        }

        return <code className={`block bg-gray-100 p-2 rounded my-2 overflow-x-auto ${className || ""}`} {...props}>{children}</code>
      },
      pre: ({ node, ...rest }) => <pre className="bg-gray-100 p-2 rounded my-2 overflow-x-auto" {...rest} />,
      table: ({ node, ...rest }) => <table className="border-collapse border border-gray-300 my-2" {...rest} />,
      th: ({ node, ...rest }) => <th className="border border-gray-300 px-2 py-1 bg-gray-100" {...rest} />,
      td: ({ node, ...rest }) => <td className="border border-gray-300 px-2 py-1" {...rest} />,
    }}
    remarkPlugins={[remarkGfm, remarkImages]}
    rehypePlugins={props.highlight ? [rehypeHighlight(props.highlight)] : []}
  >{cleanedValue}</Markdown></div>

})
